/*
 * Footer draggable sticker. All position + size in % of the parent
 * `.footer-drag-layer` so the layout survives window resize: the sticker
 * scales with its container, and the dragged x/y stay proportionally
 * anchored. The drag handler converts pixel mouse deltas to % at the
 * moment of move; CSS does the rest.
 */
export default function draggableSticker({ xPct = 10, yPct = 10, rotation = 0, widthPct = 12 } = {}) {
  return {
    rotation,
    widthPct,
    xPct,
    yPct,
    loaded: false,
    ox: 0,
    oy: 0,
    dragging: false,

    get style() {
      return {
        position: 'absolute',
        left: this.xPct + '%',
        top: this.yPct + '%',
        width: this.widthPct + '%',
        transform: `rotate(${this.rotation}deg) scale(${this.dragging ? 1.05 : 1})`,
        // Animate only the transform — `left`/`top` must stay instant so the
        // sticker tracks the cursor without lag.
        transition: 'transform 180ms ease-out',
        cursor: this.dragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none',
      };
    },

    init() {
      // Reveal delay preserved from the prior implementation — stickers fade
      // in a few seconds after page load so the wordmark reads first.
      setTimeout(() => {
        this.loaded = true;
      }, 3000);
    },

    startDrag(cx, cy) {
      const rect = this.$el.getBoundingClientRect();
      this.ox = cx - rect.left;
      this.oy = cy - rect.top;
      this.dragging = true;
    },

    onMove(cx, cy) {
      if (!this.dragging) return;
      const layer = this.$el.closest('.footer-drag-layer');
      if (!layer) return;
      const bounds = layer.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;

      // Convert mouse delta → top-left in container coords (px) → %.
      // Clamp to [0, 100 - elementSizeAsPercent] so the sticker can't
      // overflow its container, then commit. Re-reading offsetWidth/Height
      // each move means the clamp respects the current rendered size even
      // if the container is mid-resize.
      const xPx = cx - bounds.left - this.ox;
      const yPx = cy - bounds.top - this.oy;
      const wPct = (this.$el.offsetWidth / bounds.width) * 100;
      const hPct = (this.$el.offsetHeight / bounds.height) * 100;
      this.xPct = Math.min(Math.max((xPx / bounds.width) * 100, 0), Math.max(100 - wPct, 0));
      this.yPct = Math.min(Math.max((yPx / bounds.height) * 100, 0), Math.max(100 - hPct, 0));
    },

    stopDrag() {
      this.dragging = false;
    },
  };
}
